import {
  UseA11yNestedNavigationArguments,
  UseA11yNestedNavigationReturnType,
} from "./types";
import {
  useA11yChildren,
  A11Y_CHILD_MIN_INDEX,
  A11Y_DEFAULT_CURRENT_INDEX,
} from "./use-a11y-children";

export const useA11yNestedNavigation = ({
  maxIndex,
  hasFocusableChildren,
}: UseA11yNestedNavigationArguments): UseA11yNestedNavigationReturnType => {
  const {
    a11yChildRefs,
    a11yCurrentChildIndex,
    setA11yChildRefs,
    setA11yCurrentChildIndex,
  } = useA11yChildren();

  const canNavigateChildren =
    a11yCurrentChildIndex > A11Y_DEFAULT_CURRENT_INDEX;

  const setFocusToChild = (index: number) => {
    if (hasFocusableChildren) {
      a11yChildRefs.current[index]?.focus();
    }
  };

  const handleEnter = () => {
    if (!canNavigateChildren) {
      setA11yCurrentChildIndex(A11Y_CHILD_MIN_INDEX);

      setFocusToChild(A11Y_CHILD_MIN_INDEX);
    } else {
      a11yChildRefs.current[a11yCurrentChildIndex]?.click();
    }
  };

  const handleAfterBlur = () => {
    setA11yCurrentChildIndex(A11Y_DEFAULT_CURRENT_INDEX);

    a11yChildRefs.current.forEach((ref) => {
      ref.blur();
    });
  };

  const handleArrowUp = () => {
    if (canNavigateChildren) {
      setA11yCurrentChildIndex((previous) => {
        let nextIndex = previous - 1;

        if (nextIndex < A11Y_CHILD_MIN_INDEX) {
          nextIndex = maxIndex;
        }

        setFocusToChild(nextIndex);

        return nextIndex;
      });
    }
  };

  const handleArrowDown = () => {
    if (canNavigateChildren) {
      setA11yCurrentChildIndex((previous) => {
        let nextIndex = previous + 1;

        if (nextIndex > maxIndex) {
          nextIndex = A11Y_CHILD_MIN_INDEX;
        }

        setFocusToChild(nextIndex);

        return nextIndex;
      });
    }
  };

  return {
    isA11yChildActive: a11yCurrentChildIndex >= 0,
    a11yChildRefs,
    a11yCurrentChildIndex,
    setA11yChildRefs,
    setA11yCurrentChildIndex,
    handleEnter,
    handleArrowUp,
    handleArrowDown,
    handleAfterBlur,
  };
};
